import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createApp,
  deleteApp,
  discoverConsoleApps,
  getApp,
  getAppSibling,
  getDiscoveredLocales,
  getMonthlyOverviews,
  importExperiments,
  launchConsoleConnect,
  listApps,
  pairApp,
  patchApp,
  putBrief,
  searchStoreApps,
  selectApp,
  triggerBaselineRefresh,
  unpairApp,
} from "@/services/apps";
import type {
  AppCreateRequest,
  AppCreateResponse,
  AppDeleteResponse,
  AppDetailOut,
  AppListResponse,
  AppPairResponse,
  AppPatchRequest,
  AppPatchResponse,
  AppPlatform,
  AppSelectResponse,
  AppSiblingResponse,
  BaselineRefreshResponse,
  ConsoleDiscoveryResponse,
  DiscoveredLocalesResponse,
  MonthlyOverviewListResponse,
  ExperimentImportRequest,
  ExperimentImportResponse,
  LiveConnectSessionOut,
  OnboardingBriefWriteRequest,
  OnboardingBriefWriteResponse,
  StoreSearchResponse,
} from "@/types";

/**
 * Apps hooks (TanStack Query 5).
 *
 * Source: BRIEF_ia-redesign-frontend-implementation § 9 + § A6
 * + R1b detail/patch (docs/product/technical-architecture.md § App Entity).
 * Backed by services/apps.ts.
 */

const APPS_KEY_ROOT = "apps";
const APP_DETAIL_KEY_ROOT = "app-detail";
const CREATIVES_KEY_ROOT = "creatives";

export function useApps(workspaceId: string | null, userId: string | null) {
  return useQuery({
    queryKey: [APPS_KEY_ROOT, workspaceId, userId],
    queryFn: () => {
      if (!workspaceId || !userId) {
        throw new Error("workspaceId + userId required");
      }
      return listApps(workspaceId, userId);
    },
    enabled: Boolean(workspaceId && userId),
    staleTime: 30_000,
  });
}

export interface SelectAppArgs {
  appId: string;
  userId: string;
}

export function useSelectApp() {
  const queryClient = useQueryClient();
  return useMutation<AppSelectResponse, Error, SelectAppArgs>({
    mutationFn: ({ appId, userId }) =>
      selectApp(appId, { user_id: userId }, crypto.randomUUID()),
    onSuccess: (_data, variables) => {
      // Invalidate apps cache for the affected user (last_selected_at refresh).
      queryClient.invalidateQueries({
        queryKey: [APPS_KEY_ROOT],
      });
      void variables;
    },
  });
}

/**
 * `POST /api/v1/apps/{app_id}/baseline:refresh` mutation hook — the operator
 * Re-baseline button on Creatives (ENG-CONTENT-SCRAPE-TRIGGER-REDESIGN C2).
 *
 * Queues the CHEAP public-HTTP baseline re-capture (screenshots / feature
 * graphic / copy). The re-fetch is async, so on success we invalidate the
 * creatives + app-detail caches: the next natural refetch picks up the fresh
 * baseline once the worker completes. A fresh `crypto.randomUUID()` per call
 * makes a double-click safe (the backend replays the recorded 202).
 */
export function useRebaselineApp() {
  const queryClient = useQueryClient();
  return useMutation<BaselineRefreshResponse, Error, { appId: string }>({
    mutationFn: ({ appId }) =>
      triggerBaselineRefresh(appId, crypto.randomUUID()),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CREATIVES_KEY_ROOT] });
      queryClient.invalidateQueries({
        queryKey: [APP_DETAIL_KEY_ROOT, variables.appId],
      });
    },
  });
}

/**
 * Convenience: extract the AppSummaryOut for the currently-selected app, if any.
 */
export function selectedAppFromList(
  data: AppListResponse | undefined,
  selectedAppId: string | null,
) {
  if (!data || !selectedAppId) return null;
  return data.apps?.find((app) => app.app_id === selectedAppId) ?? null;
}

export interface UseAppDetailOptions {
  /**
   * Optional polling cadence in milliseconds. Pass a number to enable
   * automatic refetch (Onboarding Step 3 polls for ``baseline_last_fetched_at``
   * to arrive once the store-pipeline scrape worker completes). Pass
   * ``false`` or omit to disable polling.
   */
  refetchInterval?: number | false;
}

/**
 * Read the full App entity (R1b column set) for a single app.
 *
 * Powers Settings → Confidence & Speed (R11), the Mode badge in the
 * global Header, the Onboarding Step 3 baseline-arrival poll, and any
 * other surface that needs the lifecycle mode / automation flags /
 * quality-gate fields beyond what `AppSummaryOut` carries.
 */
export function useAppDetail(
  appId: string | null,
  options: UseAppDetailOptions = {},
) {
  return useQuery({
    queryKey: [APP_DETAIL_KEY_ROOT, appId],
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return getApp(appId);
    },
    enabled: Boolean(appId),
    staleTime: 15_000,
    refetchInterval: options.refetchInterval ?? false,
  });
}

const APP_SIBLING_KEY_ROOT = "app-sibling";

/**
 * ENG-FE-V2-CROSS-APP-PROGRAM-SHARING — fetch the Android/iOS sibling
 * App entry for the target appId.
 *
 * Returns an `AppSiblingResponse` where `sibling_app_id === null` is
 * the normal state for single-platform products. Callers render the
 * "Share with sibling" toggle only when `sibling_app_id !== null`.
 */
export function useAppSibling(appId: string | null) {
  return useQuery<AppSiblingResponse>({
    queryKey: [APP_SIBLING_KEY_ROOT, appId],
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return getAppSibling(appId);
    },
    enabled: Boolean(appId),
    // Sibling-pair relationship rarely changes within a session
    // (would require a parallel app-create); 5-min cache is generous
    // without making the toggle visibly stale post-app-add.
    staleTime: 5 * 60_000,
  });
}

const DISCOVERED_LOCALES_KEY_ROOT = "discovered-locales";

/**
 * ENG-LOCALE-AUTO-DISCOVERY — fetch the app's auto-discovered target-locale
 * universe for the experiment / program locale dropdown. Replaces the prior
 * hardcoded locale list / free-text entry. Returns an empty `locales` array
 * until the app's store analytics are connected; callers union the result with
 * the operator's current selection so a chosen locale stays selectable.
 */
export function useDiscoveredLocales(appId: string | null) {
  return useQuery<DiscoveredLocalesResponse>({
    queryKey: [DISCOVERED_LOCALES_KEY_ROOT, appId],
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return getDiscoveredLocales(appId);
    },
    enabled: Boolean(appId),
    staleTime: 60_000,
  });
}

const CONSOLE_DISCOVERY_KEY_ROOT = "console-discovery";

/**
 * ENG-ONBOARDING-LIVE-CONNECT sub-4 — discover the apps the workspace's
 * connected store-console session exposes, for the Onboarding Screen-1 picker.
 * Enabled once the operator chooses a platform. The endpoint is dormant
 * (`discovery_pending=true` + `apps=[]`) until the fixture-gated list-apps
 * scrape adapter ships, so today this returns the dormant shape; the panel
 * reads `discovery_pending` to keep the operator on the connect step rather
 * than rendering an empty picker. When the scrape lands the same hook surfaces
 * the discovered apps with no caller change.
 */
export function useConsoleAppDiscovery(
  workspaceId: string | null,
  platform: AppPlatform | null,
) {
  return useQuery<ConsoleDiscoveryResponse>({
    queryKey: [CONSOLE_DISCOVERY_KEY_ROOT, workspaceId, platform],
    queryFn: () => {
      if (!workspaceId || !platform) {
        throw new Error("workspaceId + platform required");
      }
      return discoverConsoleApps(workspaceId, platform);
    },
    enabled: Boolean(workspaceId && platform),
    staleTime: 30_000,
  });
}

const STORE_APP_SEARCH_KEY_ROOT = "store-app-search";

/**
 * ENG-ONBOARDING-4SCREEN-SEARCH-CONNECT — dual-store app-name search for the
 * onboarding Screen-1 pick dropdown (`GET /api/v1/apps/store-search`;
 * experience-and-ui.md § 9c Search-and-pick). The caller debounces the query
 * (the component keeps a debounced copy of the input); the hook gates on
 * ≥2 chars per the endpoint's `min_length`. `placeholderData` keeps the
 * previous candidate list rendered while the next keystroke's results load,
 * so the dropdown doesn't flicker empty between queries.
 */
export function useStoreAppSearch(query: string) {
  const q = query.trim();
  return useQuery<StoreSearchResponse, Error>({
    queryKey: [STORE_APP_SEARCH_KEY_ROOT, q],
    queryFn: () => searchStoreApps(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * Launch a self-hosted live-login console-connect session (POST
 * /api/v1/console-connect/sessions, ENG-ONBOARDING-LIVE-CONNECT sub-1/2). The
 * onboarding Connect button calls this; the returned `state` drives the panel
 * (`awaiting_login` → live viewport; `runner_unavailable`/`failed` → fall back
 * to the manual secure connect). Dormant until the Fly headful runner ships.
 */
export function useLaunchConsoleConnect() {
  return useMutation<
    LiveConnectSessionOut,
    Error,
    { workspaceId: string; platform: AppPlatform }
  >({
    mutationFn: ({ workspaceId, platform }) =>
      launchConsoleConnect(workspaceId, platform),
  });
}

export interface PatchAppArgs {
  appId: string;
  body: AppPatchRequest;
}

/**
 * Partial update of mutable R1b columns. The handler emits a
 * ``mode_transitioned`` outbox event atomically when ``system_mode`` or
 * ``operator_mode_override`` change.
 */
export function usePatchApp() {
  const queryClient = useQueryClient();
  return useMutation<AppPatchResponse, Error, PatchAppArgs>({
    mutationFn: ({ appId, body }) => patchApp(appId, body, crypto.randomUUID()),
    onSuccess: (data, variables) => {
      // Optimistic cache update for the detail query, then invalidate
      // both the detail and summary list (mode + appetite reflect into
      // the AppSummaryOut shape). The PATCH echo never re-prices the
      // slider consequence row (`mode_revenue_bands` is filled by the GET
      // read only — ENG-REVENUE-BAND-NEVER-RENDERS Track E), so carry the
      // cached rows across the optimistic update; the invalidate below
      // refreshes them from the read. Otherwise the row blanks on every
      // slider move until the refetch lands.
      queryClient.setQueryData<AppDetailOut>(
        [APP_DETAIL_KEY_ROOT, variables.appId],
        (previous) => ({
          ...data.app,
          mode_revenue_bands:
            data.app.mode_revenue_bands &&
            data.app.mode_revenue_bands.length > 0
              ? data.app.mode_revenue_bands
              : (previous?.mode_revenue_bands ?? []),
        }),
      );
      queryClient.invalidateQueries({
        queryKey: [APP_DETAIL_KEY_ROOT, variables.appId],
      });
      queryClient.invalidateQueries({ queryKey: [APPS_KEY_ROOT] });
    },
  });
}

export interface PutBriefArgs {
  appId: string;
  body: OnboardingBriefWriteRequest;
}

/**
 * Full-replacement brief write (ENG-M2-CLOSE Sub-3). Used by
 * Onboarding Step 2 to land the brief atomically + trigger the inline
 * analyze_brief pipeline. Distinct from usePatchApp's brief-update
 * path: PUT emits `brief_context_updated` (4-mandatory cache canon)
 * and `signals_added` reflects the real per-call contribution count.
 */
export function usePutBrief() {
  const queryClient = useQueryClient();
  return useMutation<OnboardingBriefWriteResponse, Error, PutBriefArgs>({
    mutationFn: ({ appId, body }) => putBrief(appId, body, crypto.randomUUID()),
    onSuccess: (_data, variables) => {
      // brief_version + onboarding_brief changed on the App row +
      // last_brief_reanalyzed_at bumped → invalidate detail + summary.
      queryClient.invalidateQueries({
        queryKey: [APP_DETAIL_KEY_ROOT, variables.appId],
      });
      queryClient.invalidateQueries({ queryKey: [APPS_KEY_ROOT] });
    },
  });
}

export interface ImportExperimentsArgs {
  appId: string;
  body: ExperimentImportRequest;
}

/**
 * Imported-experiment-history batch trigger (HTTP_API.md
 * `POST /api/v1/apps/{app_id}/experiments/import`;
 * ENG-EXPERIMENTS-LAUNCH-READINESS item 3, audit #19). Queues the
 * batch into the `experiment_import` worker; invalidates the
 * experiments list root so the History page's Imported rows appear as
 * the worker finalizes them. The worker is idempotent on deterministic
 * ids, so a re-import of the same batch is safe.
 */
export function useImportExperiments() {
  const queryClient = useQueryClient();
  return useMutation<ExperimentImportResponse, Error, ImportExperimentsArgs>({
    mutationFn: ({ appId, body }) =>
      importExperiments(appId, body, crypto.randomUUID()),
    onSuccess: () => {
      // Key root mirrors useExperiments.ts EXPERIMENTS_KEY_ROOT.
      queryClient.invalidateQueries({ queryKey: ["experiments"] });
    },
  });
}

export interface DeleteAppArgs {
  appId: string;
}

/**
 * Hard-delete an app row (ENG-M2-CLOSE Sub-2). Used by Settings →
 * Store connections § Danger zone. Cascades 12 child tables via
 * Postgres ON DELETE CASCADE; 3 RESTRICT child tables surface as
 * HTTP 409 `app_has_active_children` — callers MUST handle that
 * branch and surface the `failed_constraint` name from the error
 * envelope so the operator knows what to archive.
 */
export function useDeleteApp() {
  const queryClient = useQueryClient();
  return useMutation<AppDeleteResponse, Error, DeleteAppArgs>({
    mutationFn: ({ appId }) => deleteApp(appId, crypto.randomUUID()),
    onSuccess: (_data, variables) => {
      // Drop the detail cache for the deleted app + refresh the
      // workspace summary list (the deleted app MUST disappear from
      // the tree selector + login modal on the next read).
      queryClient.removeQueries({
        queryKey: [APP_DETAIL_KEY_ROOT, variables.appId],
      });
      queryClient.invalidateQueries({ queryKey: [APPS_KEY_ROOT] });
    },
  });
}

/**
 * Create an App row from the Onboarding Step 1 URL paste. The backend
 * parses platform + store_id from the URL; listing baseline metadata is
 * populated asynchronously by the store-pipeline worker.
 */
export function useCreateApp() {
  const queryClient = useQueryClient();
  return useMutation<AppCreateResponse, Error, AppCreateRequest>({
    mutationFn: (body) => createApp(body, crypto.randomUUID()),
    onSuccess: (data) => {
      // Seed the detail cache for the new app and refresh the workspace
      // summary list so the freshly-created app shows up immediately.
      queryClient.setQueryData<AppDetailOut>(
        [APP_DETAIL_KEY_ROOT, data.app.app_id],
        data.app,
      );
      queryClient.invalidateQueries({ queryKey: [APPS_KEY_ROOT] });
    },
  });
}

export interface PairAppArgs {
  appId: string;
  counterpartAppId: string;
}

/**
 * ENG-APP-PAIRING — explicitly link the app to its opposite-platform
 * counterpart (Store-connections Pair flow). The symmetric write touches both
 * rows + flips `/sibling` resolution, so invalidate ALL app-detail + sibling
 * queries (not just the target's) plus the summary list.
 */
export function usePairApp() {
  const queryClient = useQueryClient();
  return useMutation<AppPairResponse, Error, PairAppArgs>({
    mutationFn: ({ appId, counterpartAppId }) =>
      pairApp(appId, counterpartAppId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APP_DETAIL_KEY_ROOT] });
      queryClient.invalidateQueries({ queryKey: [APP_SIBLING_KEY_ROOT] });
      queryClient.invalidateQueries({ queryKey: [APPS_KEY_ROOT] });
    },
  });
}

/**
 * ENG-APP-PAIRING — clear the operator-linked pair on both sides. Same
 * cache-invalidation fan-out as `usePairApp` (both rows change).
 */
export function useUnpairApp() {
  const queryClient = useQueryClient();
  return useMutation<AppPairResponse, Error, { appId: string }>({
    mutationFn: ({ appId }) => unpairApp(appId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APP_DETAIL_KEY_ROOT] });
      queryClient.invalidateQueries({ queryKey: [APP_SIBLING_KEY_ROOT] });
      queryClient.invalidateQueries({ queryKey: [APPS_KEY_ROOT] });
    },
  });
}

const MONTHLY_OVERVIEWS_KEY_ROOT = "monthly-overviews";

/**
 * The app's frozen monthly AI overviews, newest-first (ASOLOOP-UX item 12
 * 3/3 — HTTP_API.md `GET /api/v1/apps/{app_id}/monthly-overviews`).
 * `enabled` gates the fetch so the Dashboard timeline only loads the history
 * when the operator actually opens the panel. Long staleTime — the rows are
 * IMMUTABLE server-side; only a new month (cron) or backfill adds entries.
 */
export function useMonthlyOverviews(appId: string | null, enabled: boolean) {
  return useQuery<MonthlyOverviewListResponse>({
    queryKey: [MONTHLY_OVERVIEWS_KEY_ROOT, appId],
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return getMonthlyOverviews(appId);
    },
    enabled: Boolean(appId) && enabled,
    staleTime: 5 * 60_000,
  });
}
